<script setup>
const options = { year: "numeric", month: "numeric", day: "numeric" };

const getDateFormat = (date) => {
  date = new Date(date * 1000);
  if (date.getYear() < 2200) {
    return date.toLocaleDateString(undefined, options);
  }
};
</script>

<template>
  <div class="card mt-3">
    <!-- Barre de contrôles -->
    <div class="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <!-- Switch vue Cards / Tableau -->
      <div class="flex gap-2" id="licences-barre-vue">
        <Button
          id="btn-cards-view"
          icon="pi pi-th-large"
          :label="$t('licences.cardView')"
          :outlined="viewMode !== 'cards'"
          :severity="viewMode === 'cards' ? 'primary' : 'secondary'"
          @click="viewMode = 'cards'"
          size="small"
        />
        <Button
          id="btn-table-view"
          icon="pi pi-list"
          :label="$t('licences.tableView')"
          :outlined="viewMode !== 'table'"
          :severity="viewMode === 'table' ? 'primary' : 'secondary'"
          @click="viewMode = 'table'"
          size="small"
        />
        <Button
          id="btn-download-csv"
          v-if="viewMode === 'table'"
          icon="pi pi-download"
          :label="$t('licences.downloadCsv')"
          severity="secondary"
          outlined
          size="small"
          @click="downloadCsv"
        />
      </div>
      <!-- Toggles -->
      <div class="flex align-items-center gap-4 flex-wrap">
        <div v-if="viewMode === 'cards'" class="flex align-items-center gap-2" id="licences-toggle-hidden">
          <label class="text-sm">{{ $t('licences.showHidden') }}</label>
          <InputSwitch v-model="showHidden" />
        </div>
        <div class="flex align-items-center gap-2" id="licences-toggle-expired">
          <label class="text-sm">{{ $t('licences.showExpired') }}</label>
          <InputSwitch v-model="showExpired" />
        </div>
      </div>
    </div>

    <!-- Vue Cards -->
    <div v-if="viewMode === 'cards'" class="flex flex-wrap gap-3" id="licences-cards">
      <Card
        v-for="group in visibleGroups"
        :key="`${group.num_serie}_${group.part}`"
        style="width: 22rem; min-width: 18rem"
        :class="['border-1 border-gray-200 shadow-2', isHidden(group) ? 'opacity-50' : '']"
      >
        <template #title>
          <div class="flex justify-content-between align-items-center">
            <div>
              <span class="text-base font-bold">{{ group.num_serie }}</span>
              <span class="text-sm text-color-secondary ml-2">{{ $t('licences.partition') }} {{ group.part }}</span>
            </div>
            <Button
              :icon="isHidden(group) ? 'pi pi-eye' : 'pi pi-eye-slash'"
              :title="isHidden(group) ? $t('licences.showGroup') : $t('licences.hideGroup')"
              text
              rounded
              size="small"
              severity="secondary"
              @click="toggleHideGroup(group)"
            />
          </div>
        </template>
        <template #subtitle>
          <span v-if="group.description" class="text-sm">{{ group.description }}</span>
        </template>
        <template #footer>
          <div class="flex gap-2 justify-content-end">
            <Button
              icon="pi pi-copy"
              :label="$t('licences.copy5250')"
              size="small"
              severity="secondary"
              outlined
              @click="copy5250(group)"
            />
            <Button
              icon="pi pi-eye"
              :label="$t('licences.viewDetails')"
              size="small"
              @click="openDetail(group)"
            />
          </div>
        </template>
      </Card>
    </div>

    <!-- Vue Tableau -->
    <DataTable
      v-else
      id="licences"
      :value="filteredLicences"
      paginator
      :rows="5"
      sortField="date_debut"
      :sortOrder="-1"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      tableStyle="min-width: 50rem"
      filterDisplay="row"
      v-model:filters="filters"
      :pt="{
        root: () => ({
          class: 'border-2 border-gray-300',
        }),
      }"
    >
      <Column
        field="numContrat"
        :header="$t('licences.contractNumber')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.contractNumber')"
            style="min-width: 6rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="productLabel"
        :header="$t('licences.productName')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.productName')"
            style="min-width: 6rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="groupe_processeur"
        :header="$t('licences.processeurGroup')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.processeurGroup')"
            style="min-width: 5rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="cle"
        :header="$t('licences.key')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.key')"
            style="min-width: 8rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="num_serie"
        :header="$t('licences.serialName')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.serialName')"
            style="min-width: 6rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="part"
        :header="$t('licences.partition')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.partition')"
            style="min-width: 4rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="description"
        :header="$t('licences.description')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.description')"
            style="min-width: 6rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="date_debut_fmt"
        :header="$t('licences.creationDate')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.creationDate')"
            style="min-width: 6rem"
            size="small"
          />
        </template>
      </Column>
      <Column
        field="date_fin_fmt"
        :header="$t('licences.expirationDate')"
        :showFilterMenu="false"
        :pt="colPt"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            class="p-column-filter"
            :placeholder="$t('licences.expirationDate')"
            style="min-width: 6rem"
            size="small"
          />
        </template>
      </Column>
    </DataTable>

    <!-- Dialog détail d'un groupe -->
    <Dialog
      v-model:visible="showDetailDialog"
      :header="`${selectedGroup ? selectedGroup.num_serie : ''} — ${$t('licences.partition')} ${selectedGroup ? selectedGroup.part : ''}`"
      modal
      :style="{ width: '90vw', maxWidth: '1200px' }"
    >
      <DataTable
        v-if="selectedGroup"
        :value="selectedGroup.licences"
        paginator
        :rows="10"
        sortField="date_debut"
        :sortOrder="-1"
        :rowsPerPageOptions="[5, 10, 20]"
        tableStyle="min-width: 40rem"
        :pt="{
          root: () => ({ class: 'border-2 border-gray-300' }),
        }"
      >
        <Column field="numContrat" :header="$t('licences.contractNumber')" :pt="colPt"></Column>
        <Column :header="$t('licences.productName')" :pt="colPt">
          <template #body="slotProps">
            {{
              tableau[slotProps.data.codeProduit] !== undefined
                ? tableau[slotProps.data.codeProduit]["label"]
                : slotProps.data.codeProduit
            }}
          </template>
        </Column>
        <Column field="groupe_processeur" :header="$t('licences.processeurGroup')" :pt="colPt"></Column>
        <Column field="cle" :header="$t('licences.key')" :pt="colPt"></Column>
        <Column field="description" :header="$t('licences.description')" :pt="colPt"></Column>
        <Column field="date_debut" :header="$t('licences.creationDate')" :pt="colPt">
          <template #body="slotProps">
            {{
              getDateFormat(slotProps.data.date_debut) === "01/01/1970"
                ? "Non défini"
                : getDateFormat(slotProps.data.date_debut)
            }}
          </template>
        </Column>
        <Column field="date_fin" :header="$t('licences.expirationDate')" :pt="colPt">
          <template #body="slotProps">
            {{
              getDateFormat(slotProps.data.date_fin)
                ? getDateFormat(slotProps.data.date_fin)
                : "Permanent"
            }}
          </template>
        </Column>
      </DataTable>
    </Dialog>
  </div>

  <Button
    class="fixed border-circle shadow-2 bg-blue-500 hover:bg-blue-700 text-white text-xl flex align-items-center justify-content-center"
    style="bottom: 20px; right: 20px; z-index: 9999"
    @click="launchGuide('compte_licences')"
  >
    ?
  </Button>
</template>

<script>
import axios from "axios";
import { startTour } from "@/guide/guide";
import { FilterMatchMode } from "primevue/api";

export default {
  data() {
    return {
      licences: null,
      idTiers: null,
      libelles: null,
      tableau: {},
      viewMode: "cards",
      showExpired: false,
      showHidden: false,
      _hiddenStorageKey: `hiddenMachines_${localStorage.getItem('idDolibarr')}_${localStorage.getItem('idTiers')}`,
      hiddenGroups: JSON.parse(localStorage.getItem(`hiddenMachines_${localStorage.getItem('idDolibarr')}_${localStorage.getItem('idTiers')}`) || '[]'),
      _effectiveTiersId: localStorage.getItem('idTiersPayeur') !== 'undefined' ? localStorage.getItem('idTiersPayeur') : localStorage.getItem('idTiers'),
      selectedGroup: null,
      showDetailDialog: false,
      filters: {
        numContrat: { value: null, matchMode: FilterMatchMode.CONTAINS },
        productLabel: { value: null, matchMode: FilterMatchMode.CONTAINS },
        groupe_processeur: { value: null, matchMode: FilterMatchMode.CONTAINS },
        cle: { value: null, matchMode: FilterMatchMode.CONTAINS },
        num_serie: { value: null, matchMode: FilterMatchMode.CONTAINS },
        part: { value: null, matchMode: FilterMatchMode.CONTAINS },
        description: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date_debut_fmt: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date_fin_fmt: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      colPt: {
        headerCell: () => ({
          class:
            "bg-white border-top-none border-right-none border-left-none border-dashed border-2",
        }),
        bodyCell: () => ({
          class:
            "bg-white border-top-none border-right-none border-left-none border-dashed border-2",
        }),
      },
    };
  },
  computed: {
    filteredLicences() {
      if (!this.licences) return [];
      const now = Math.floor(Date.now() / 1000);
      const options = { year: "numeric", month: "numeric", day: "numeric" };
      const list = this.showExpired
        ? this.licences
        : this.licences.filter((l) => !l.date_fin || l.date_fin > now);
      // Enrichir chaque ligne avec des champs filtrables calculés
      return list.map((l) => ({
        ...l,
        productLabel:
          this.tableau[l.codeProduit]?.label ?? l.codeProduit,
        date_debut_fmt: l.date_debut
          ? new Date(l.date_debut * 1000).toLocaleDateString(undefined, options)
          : "",
        date_fin_fmt: l.date_fin
          ? new Date(l.date_fin * 1000).toLocaleDateString(undefined, options)
          : "Permanent",
      }));
    },
    licenceGroups() {
      const map = {};
      for (const l of this.filteredLicences) {
        const key = `${l.num_serie}__${l.part}`;
        if (!map[key]) {
          map[key] = {
            num_serie: l.num_serie,
            part: l.part,
            description: l.description,
            licences: [],
          };
        }
        map[key].licences.push(l);
      }
      return Object.values(map);
    },
    visibleGroups() {
      if (this.showHidden) return this.licenceGroups;
      return this.licenceGroups.filter((g) => !this.isHidden(g));
    },
  },
  mounted() {
    // s'il y a un tiers payeur on prend son identifiant pour les licences
    if (localStorage.getItem("idTiersPayeur") !== "undefined") {
      this.idTiers = localStorage.getItem("idTiersPayeur");
    } else {
      this.idTiers = localStorage.getItem("idTiers");
    }
    //récupération du token backend dans le local storage
    this.token = localStorage.getItem("token");
    this.tableau = JSON.parse(localStorage.getItem("libellesProduitsPHL")) || {};
    // Charger les machines masquées depuis la BD
    axios.get(process.env.VUE_APP_URL_BACK_END + "/api/preferences/hidden-machines", {
      params: { tiersId: this._effectiveTiersId },
      headers: { Authorization: "Bearer " + this.token },
    }).then((res) => {
      const fromDb = res.data.hiddenMachines || [];
      if (fromDb.length > 0) {
        this.hiddenGroups = fromDb;
        localStorage.setItem(this._hiddenStorageKey, JSON.stringify(fromDb));
      }
    }).catch(() => { /* conserver les valeurs localStorage en cas d'erreur */ });
    axios
      .post(
        process.env.VUE_APP_URL_BACK_END + "/api/dolibarr/licences",
        { idTiers: this.idTiers },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + this.token,
          },
        }
      )
      .then((response) => {
        //Permet d'éviter un vue warn qui préfère un tableau vide à la place d'un string vide
        if (response["data"] !== undefined && response["data"] !== "") {
          this.licences = response["data"]["licences"];
          this.libelles = response["data"]["libelles"];
        } else {
          this.licences = [];
        }
      })
      .catch((e) => {
        //Token invalide ou expiré
        if (e["response"]["status"] === 401) {
          this.$router.push("/login");
        } else { //capture des erreurs revoyées par le back-end
          this.$toast.add({
            severity: "error",
            summary: this.$t("erreur.erreur"),
            detail: this.$t("erreur.erreurInterne"),
            life: 3000,
          });
        }
      });
  },
  methods: {
    launchGuide(guideContext) {
      startTour(guideContext, this.$i18n.locale);
    },
    openDetail(group) {
      this.selectedGroup = group;
      this.showDetailDialog = true;
    },
    groupKey(group) {
      return `${group.num_serie}__${group.part}`;
    },
    isHidden(group) {
      return this.hiddenGroups.includes(this.groupKey(group));
    },
    toggleHideGroup(group) {
      const key = this.groupKey(group);
      const idx = this.hiddenGroups.indexOf(key);
      if (idx === -1) {
        this.hiddenGroups.push(key);
      } else {
        this.hiddenGroups.splice(idx, 1);
      }
      localStorage.setItem(this._hiddenStorageKey, JSON.stringify(this.hiddenGroups));
      axios.put(process.env.VUE_APP_URL_BACK_END + "/api/preferences/hidden-machines", {
        tiersId: parseInt(this._effectiveTiersId),
        hiddenMachines: this.hiddenGroups,
      }, {
        headers: { Authorization: "Bearer " + this.token },
      }).catch(() => { /* silencieux, localStorage fait office de fallback */ });
    },
    copy5250(group) {
      // Liste des 12 slots AS400 dans l'ordre exact
      const AS400_SLOTS = [
        "PHL Print",
        "PHL Web",
        "PHL EXCHANGE",
        "PHL Mobile",
        "PHL Query",
        "PHL Revamp",
        "PHL 5250",
        "PHL PRINT Runtime",
        "PHL QUERY Runtime",
        "PHL SPOOL Runtime",
        "PHL WEB Runtime",
        "PHL XML Runtime",
      ];

      // Pour chaque slot, trouver tous les codeProduit dont le label correspond (insensible à la casse)
      const slotCodes = AS400_SLOTS.map((slotName) => {
        const slotLower = slotName.toLowerCase();
        return Object.entries(this.tableau)
          .filter(([, entry]) => (entry.label || "").toLowerCase() === slotLower)
          .map(([code]) => code);
      });

      // Indexer les licences du groupe par codeProduit (garder la plus récente)
      const licenceByProduct = {};
      for (const l of group.licences) {
        if (
          !licenceByProduct[l.codeProduit] ||
          l.date_debut > licenceByProduct[l.codeProduit].date_debut
        ) {
          licenceByProduct[l.codeProduit] = l;
        }
      }

      // Une ligne par slot AS400 : clé si une licence existe pour ce slot, sinon vide
      const lines = slotCodes.map((codes) => {
        for (const code of codes) {
          if (licenceByProduct[code]) return licenceByProduct[code].cle;
        }
        return "";
      });

      // Supprimer les lignes vides en fin uniquement
      while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();

      const text = lines.join("\n");

      const showSuccess = () => {
        this.$toast.add({
          severity: "success",
          summary: this.$t("licences.copiedToClipboard"),
          life: 2000,
        });
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(showSuccess).catch(() => {
          this.fallbackCopy(text, showSuccess);
        });
      } else {
        this.fallbackCopy(text, showSuccess);
      }
    },
    fallbackCopy(text, callback) {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      try {
        document.execCommand("copy");
        callback();
      } catch (e) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("erreur.erreur"),
          detail: "Impossible de copier dans le presse-papiers",
          life: 3000,
        });
      } finally {
        document.body.removeChild(el);
      }
    },
    downloadCsv() {
      const columns = [
        { field: "numContrat",        header: this.$t("licences.contractNumber") },
        { field: "productLabel",      header: this.$t("licences.productName") },
        { field: "groupe_processeur", header: this.$t("licences.processeurGroup") },
        { field: "cle",               header: this.$t("licences.key") },
        { field: "num_serie",         header: this.$t("licences.serialName") },
        { field: "part",              header: this.$t("licences.partition") },
        { field: "description",       header: this.$t("licences.description") },
        { field: "date_debut_fmt",    header: this.$t("licences.creationDate") },
        { field: "date_fin_fmt",      header: this.$t("licences.expirationDate") },
      ];
      const activeFilters = Object.entries(this.filters)
        .filter(([, f]) => f.value !== null && f.value !== "");
      const data = this.filteredLicences.filter((row) =>
        activeFilters.every(([key, f]) =>
          String(row[key] ?? "").toLowerCase().includes(f.value.toLowerCase())
        )
      );
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const rows = [
        columns.map((c) => escape(c.header)).join(";"),
        ...data.map((row) =>
          columns.map((c) => escape(row[c.field])).join(";")
        ),
      ];
      const blob = new Blob(["﻿" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "licences.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
